import React, { useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { PROFILE_CONSTANTS } from '../constants/profileConstants';
import { normalizeArrayField } from '../utils/dataProcessing';
import { SUPPORTED_CURRENCIES } from '../../../utils/currencyUtils';

const PreferencesTab = ({ 
  profileData, 
  fieldErrors, 
  onInputChange, 
  onArrayAdd, 
  onArrayRemove 
}) => {
  // Safely get normalized arrays
  const preferredProjectTypes = normalizeArrayField(profileData.preferred_project_types);
  const workSamples = normalizeArrayField(profileData.work_samples);

  // Handle project type toggle
  const handleProjectTypeToggle = useCallback((type) => {
    if (preferredProjectTypes.includes(type)) {
      onArrayRemove('preferred_project_types', type);
    } else {
      onArrayAdd('preferred_project_types', type);
    }
  }, [preferredProjectTypes, onArrayAdd, onArrayRemove]);

  return (
    <div className="space-y-8">
      
      {/* Work Preferences */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-6">Work Preferences</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Availability Status */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Availability Status
            </label>
            <select
              value={profileData.availability_status || 'available'}
              onChange={(e) => onInputChange('availability_status', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {PROFILE_CONSTANTS.AVAILABILITY_STATUSES.map(status => (
                <option key={status} value={status}>
                  {status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          {/* Preferred Work Type */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Preferred Work Type
            </label>
            <select
              value={profileData.preferred_work_type || 'remote'}
              onChange={(e) => onInputChange('preferred_work_type', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {PROFILE_CONSTANTS.WORK_TYPES.map(type => (
                <option key={type} value={type}>
                  {type.replace(/\b\w/g, l => l.toUpperCase())}
                </option>
              ))}
            </select>
          </div>

          {/* Communication Style */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Communication Style
            </label>
            <select
              value={profileData.communication_style || 'professional'}
              onChange={(e) => onInputChange('communication_style', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {PROFILE_CONSTANTS.COMMUNICATION_STYLES.map(style => (
                <option key={style} value={style}>
                  {style.charAt(0).toUpperCase() + style.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Availability Hours */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Availability Hours
            </label>
            <input
              type="text"
              value={profileData.availability_hours || ''}
              onChange={(e) => onInputChange('availability_hours', e.target.value)}
              placeholder="e.g., 9 AM - 5 PM EST, Mon-Fri"
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Preferred Currency */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Preferred Currency
            </label>
            <select
              value={profileData.preferred_currency || 'USD'}
              onChange={(e) => onInputChange('preferred_currency', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {Object.entries(SUPPORTED_CURRENCIES).map(([code, info]) => (
                <option key={code} value={code}>
                  {info.symbol} {code} - {info.name}
                </option>
              ))}
            </select>
            <p className="text-text-muted text-xs mt-1">
              Job salaries will be displayed in your preferred currency when possible
            </p>
          </div>
        </div>
      </div>

      {/* Project Type Preferences */}
      <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-6">Preferred Project Types</h3>
        <div className="space-y-4">
          <p className="text-text-secondary text-sm">
            Select the types of projects you prefer to work on:
          </p>
          <div className="flex flex-wrap gap-3">
            {PROFILE_CONSTANTS.PROJECT_TYPES.map((type) => {
              const isSelected = preferredProjectTypes.includes(type);
              return (
                <button
                  key={type}
                  onClick={() => handleProjectTypeToggle(type)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors border ${
                    isSelected
                      ? 'bg-primary text-white border-primary'
                      : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-primary-50 hover:text-primary-700'
                  }`}
                >
                  <Icon 
                    name={isSelected ? "Check" : "Plus"} 
                    size={14} 
                    className="inline mr-1" 
                  />
                  {type}
                </button>
              );
            })}
          </div>
          <div className="mt-4">
            <p className="text-text-muted text-sm">
              Selected: {preferredProjectTypes.length} project types
            </p>
          </div>
        </div>
      </div>

      {/* Assessment Scores Display */}
      {profileData.assessment_scores && Object.keys(profileData.assessment_scores).length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-border shadow-sm">
          <h3 className="text-lg font-semibold text-text-primary mb-6">Assessment Scores</h3>
          <div className="space-y-3">
            {Object.entries(profileData.assessment_scores).map(([skill, score]) => (
              <div key={skill} className="flex items-center justify-between">
                <span className="text-text-primary font-medium">{typeof skill === 'object' && skill !== null ? (skill.name || skill.skill || 'Unknown') : skill}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${score}%` }}
                    />
                  </div>
                  <span className="text-sm text-text-secondary font-medium">{score}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreferencesTab;

